import { cn } from "@chams-pay/design-system/lib/utils";
import { deploymentProductName } from "../core/deployment.js";

/**
 * The deployment mark ships as /brand-logo.png and is recoloured with a CSS mask
 * (handoff spec §2) — one asset serves the three permitted colourways:
 * primary on light (default), white on primary, ink on neutral. Never rotated,
 * outlined, drop-shadowed, or placed on a photo. Minimum size 20px.
 */
export function LogoGlyph({ className }: { readonly className?: string }) {
  return <span aria-hidden="true" className={cn("chams-logo size-6 bg-primary", className)} />;
}

/** Tile treatment: deployment-primary rounded square carrying the white mark. */
export function BrandMark({ className }: { readonly className?: string }) {
  return (
    <span
      className={cn("grid size-9 shrink-0 place-items-center rounded-lg bg-primary", className)}
      aria-hidden="true"
    >
      <LogoGlyph className="size-[62%] bg-white" />
    </span>
  );
}

/** Product app icon for install surfaces and the desktop application shell. */
export function AppIcon({ className }: { readonly className?: string }) {
  return (
    <img
      alt=""
      aria-hidden="true"
      className={cn("size-9 shrink-0 select-none", className)}
      draggable={false}
      height="64"
      src="/app-icon-64.png"
      width="64"
    />
  );
}

export function Wordmark({ className }: { readonly className?: string }) {
  const separator = deploymentProductName.lastIndexOf(".");
  const name = separator > 0 ? deploymentProductName.slice(0, separator) : deploymentProductName;
  const suffix = separator > 0 ? deploymentProductName.slice(separator) : "";
  return (
    <span
      className={cn("brand-wordmark font-heading text-base font-bold tracking-tight", className)}
      dir="ltr"
    >
      {name}
      <span className="text-primary">{suffix}</span>
    </span>
  );
}

export function BrandLockup({
  subtitle,
  className,
}: {
  readonly subtitle?: string;
  readonly className?: string;
}) {
  return (
    <span className={cn("flex items-center gap-3", className)}>
      <BrandMark />
      <span className="brand-copy flex min-w-0 flex-col items-start text-start leading-tight rtl:items-end">
        <Wordmark />
        {subtitle && <span className="truncate text-xs text-muted-foreground">{subtitle}</span>}
      </span>
    </span>
  );
}
